import { Injectable } from '@angular/core';
import { AlertInput, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ISelectionItem } from 'src/app/common/contracts/components/selection-item';
import { Deferred } from 'src/app/common/deferred/deferred';
import { SelectModalComponent } from '../inputs/select-modal/select.modal.component';

export interface ISelectionOptions {
  compareFn?: (id: any, value: any) => boolean;
  changeAllowed?: () => Promise<boolean>;
  showSearch?: boolean;
  allowMultiple?: boolean;
}
export interface ISelectionReturnObject {
  canceled: boolean;
  value: any;
}

@Injectable({
  providedIn: 'root',
})
export class SelectionService {
  private _ready: Deferred<ISelectionReturnObject>;
  private _modal: HTMLIonModalElement;

  constructor(
    private _translate: TranslateService,
    private _modalController: ModalController
  ) {}

  public async openSelection(
    currentSelected: any,
    values: ISelectionItem[],
    title: string,
    options?: ISelectionOptions
  ) {
    this._ready = new Deferred();

    const inputs = this.createInputs(currentSelected, values, options);
    // create the alert
    this._modal = await this._modalController.create({
      component: SelectModalComponent,
      backdropDismiss: false,
      componentProps: {
        header: title,
        inputs,
        buttons: this.createButtons(options),
      },
      cssClass: 'select-modal',
    });
    await this._modal.present();
    await this._modal.onDidDismiss();

    return this._ready.promise;
  }

  public checkIfChecked(currentSelected: any, value: any, compareFn?: (id: any, value: any) => boolean): boolean {
    if (compareFn && typeof compareFn === 'function') {
      return compareFn(currentSelected, value);
    }
    if (Array.isArray(currentSelected)) {
      return currentSelected.includes(value);
    }
    return currentSelected === value;
  }

  private createInputs(currentSelected: any, values: ISelectionItem[], options?: ISelectionOptions): any[] {
    const inputs = [];
    if (!values || !values.length) {
      return inputs;
    }
    values.forEach(value => {
      inputs.push({
        type: options?.allowMultiple ? 'checkbox' : 'radio',
        label: value.value,
        value: value.id,
        disabled: value.disabled,
        checked: this.checkIfChecked(
          currentSelected,
          value.id,
          options && options.compareFn ? options.compareFn : undefined
        ),
        handler: (input: AlertInput) =>
          this.saveAndClose(input, options && options.changeAllowed ? options.changeAllowed : undefined),
      });
    });
    return inputs;
  }

  public saveAndClose(input: AlertInput, changeAllowed: () => Promise<boolean>) {
    const emitChange = () => {
      this._ready.resolve({
        canceled: false,
        value: input.value,
      });
    };
    if (changeAllowed && typeof changeAllowed === 'function') {
      changeAllowed().then(allowed => {
        if (allowed) {
          emitChange();
        }
      });
    } else {
      emitChange();
    }
    this._modal.dismiss();
  }

  private createButtons(options?: ISelectionOptions) {
    if (options?.allowMultiple) {
      return [
        {
          text: this._translate.instant('DONE_BUTTON'),
          handler: (input: number[]) =>
            this.saveAndClose({ value: input }, options && options.changeAllowed ? options.changeAllowed : undefined),
        },
        {
          text: this._translate.instant('CANCEL_BUTTON'),
          handler: () => {
            this._ready.resolve({
              canceled: true,
              value: undefined,
            });
          },
        },
      ];
    }
    return [
      {
        text: this._translate.instant('CANCEL_BUTTON'),
        handler: () => {
          this._ready.resolve({
            canceled: true,
            value: undefined,
          });
        },
      },
    ];
  }
}
